import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { HelpCircle, Send, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { useApp } from '../../context/AppContext';

const API = process.env.REACT_APP_BACKEND_URL;

const QueryButton = () => {
  const location = useLocation();
  const { user } = useApp();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);

  const path = location.pathname;
  if (path.startsWith('/admin') || path.startsWith('/checkout') || /^\/messages\/.+/.test(path)) return null;

  const submit = async () => {
    const finalEmail = user?.email || email;
    if (!message.trim() || message.trim().length < 5) { toast.error('Tell us a little more so we can help'); return; }
    if (!finalEmail) { toast.error('Add your email so we can reply'); return; }
    setSending(true);
    try {
      const res = await fetch(`${API}/api/queries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: message.trim(),
          name: user?.name || name,
          email: finalEmail,
          user_id: user?.id || null,
          page: path,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Something went wrong — please try again');
      toast.success("Sent! Our support team will reply by email shortly.");
      setMessage('');
      setOpen(false);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="query-help-button"
        aria-label="Ask a question"
        className="fixed bottom-24 left-4 z-40 flex items-center gap-1.5 rounded-full shadow-lg px-3 py-2.5 text-white text-xs font-medium transition-transform hover:scale-105"
        style={{ backgroundColor: '#1a3a2a', border: '1px solid rgba(201,169,98,0.5)' }}
      >
        <HelpCircle className="w-4 h-4" style={{ color: '#c9a962' }} />
        Help
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="query-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Georgia, serif', color: '#1a3a2a' }}>Ask us anything</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-500 -mt-2">
            Questions about buying, selling, payments or your account — our support team replies by email, usually within minutes.
          </p>
          {!user && (
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Your name" value={name} onChange={e => setName(e.target.value)} data-testid="query-name-input" />
              <Input placeholder="Your email" type="email" value={email} onChange={e => setEmail(e.target.value)} data-testid="query-email-input" />
            </div>
          )}
          <Textarea
            placeholder="What can we help you with?"
            rows={4}
            value={message}
            onChange={e => setMessage(e.target.value)}
            data-testid="query-message-input"
          />
          <Button
            onClick={submit}
            disabled={sending}
            className="w-full bg-minted-600 hover:bg-minted-700"
            data-testid="query-submit-button"
          >
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Send question
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default QueryButton;
